/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { memo } from 'react';

import { useQuery } from '@apollo/client';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import { processImage } from 'js/lib/imgix';

import { Typography } from '@coursera/cds-core';

import PartnerListingHeaderStats from 'bundles/partners-graphql/components/listing/PartnerListingHeaderStats';
import PartnersQueryById from 'bundles/partners-graphql/queries/PartnersQueryById.graphql';
import type { PartnerListingPagePartnerFragment as PartnerListingPagePartner } from 'bundles/partners-graphql/queries/__generated__/GetAllPartners';
import type {
  PartnersQueryByIdQuery,
  PartnersQueryByIdQueryVariables,
} from 'bundles/partners-graphql/queries/__generated__/PartnersQueryById';

import _t from 'i18n!nls/partners-graphql';

const styles = {
  partnerListingHeader: css`
    position: relative;
    height: 240px;
    background-size: cover;
    background-position: center;

    > .bt3-row {
      max-width: 100%;
      margin: 0;
    }

    .banner-body-container {
      text-align: center;

      .banner-body {
        h1 {
          color: white;
          margin-top: 75px;
          margin-bottom: 35px;

          @media (max-width: 767px) {
            margin-top: 20px;
            margin-bottom: 20px;
          }
        }
      }
    }
  `,
};

type PropsFromCaller = {
  partners: Array<PartnerListingPagePartner>;
};

type PropsFromStore = {
  id: string;
};

type PropsToComponent = PropsFromCaller & PropsFromStore;

export const PartnerListingHeader = ({ partners, id }: PropsToComponent) => {
  const { data, loading } = useQuery<PartnersQueryByIdQuery, PartnersQueryByIdQueryVariables>(PartnersQueryById, {
    variables: {
      id,
    },
    errorPolicy: 'all',
    context: {
      clientName: 'gatewayGql',
    },
  });

  if (loading) return null;

  const featuredPartner = data?.Partner?.queryById;

  const getBackgroundImageStyle = () => {
    const backgroundImageUrl = featuredPartner?.landingPageBanner;

    const processedBackgroundImageUrl = processImage(backgroundImageUrl || '', {
      exposure: -14, // dark theme
      dpr: 1,
    });

    return { backgroundImage: `url(${processedBackgroundImageUrl})` };
  };

  return (
    <div
      css={styles.partnerListingHeader}
      style={getBackgroundImageStyle()}
      data-testid="partner-listing-header-container"
    >
      <div className="bt3-row">
        <div className="bt3-col-md-12">
          <div className="banner-body-container">
            <div className="banner-body">
              <Typography component="h1" variant="d1semibold" data-e2e="partner-listing-header">
                {_t('Meet our Partners')}
              </Typography>
            </div>
          </div>
        </div>
        <PartnerListingHeaderStats partners={partners} />
      </div>
    </div>
  );
};

// prevent this header from ever rerendering, to keep the featured partner "sticky"
const MemoizedPartnerListingHeader = memo(PartnerListingHeader, () => true);

// TODO: figure out why using MemoizedPartnerListingHeader breaks @coursera/react-ssr-prepass
const MemoizedPartnerListingHeaderWithSSRFixed = (props: PropsToComponent) => (
  <MemoizedPartnerListingHeader {...props} />
);

export default connectToStores<PropsToComponent, Omit<PropsToComponent, 'id'>>(
  ['PartnersStore'],
  ({ PartnersStore }) => ({
    id: '' + PartnersStore.getFeaturedPartnerId(),
  })
)(MemoizedPartnerListingHeaderWithSSRFixed);
